import type { MouseEvent, MouseEventHandler, ReactNode } from 'react';
import clsx from 'clsx';
import { useHref, useLocation, useNavigate, useResolvedPath, type To } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { FontWeights, Typography } from 'components/UI/Typography';
import { Colors } from 'environment';
import { resolveTrustedParentTargetOrigin } from 'features/communication-bridge/trusted-parent-origins';

export const Container = styled.div`
	display: flex;
	align-items: center;
	height: 100%;
`;

export const Text = styled(Typography.Paragraph)``;

interface TabProps {
	width: number;
	underline_width: number;
	disabled?: boolean;
}

interface DivTabProps extends TabProps {
	active: boolean;
}
export const DivTab = styled.div<DivTabProps>`
	${({ active, ...tabProps }) => css`
		${getTabStyle(tabProps)}

		${active &&
		css`
			div {
				::after {
					opacity: 1;
				}
			}

			${Text} {
				color: ${Colors.primary.normal};
				font-weight: ${FontWeights.medium};
			}
		`}
	`}
`;

const StyledNavTab = styled.a<TabProps>`
	${tabProps => css`
		${getTabStyle(tabProps)}

		&.active {
			text-decoration: none;

			div {
				::after {
					opacity: 1;
				}
			}

			${Text} {
				color: ${Colors.primary.normal};
				font-weight: ${FontWeights.medium};
			}
		}

		:hover {
			border-color: ${Colors.primary.normal}!important;
			text-decoration: none;
		}

		&:not(.active):visited {
			border-color: ${Colors.transparent};
			text-decoration: none;
		}
	`}

	${({ disabled }) =>
		disabled &&
		css`
			pointer-events: none;

			${Typography.Paragraph} {
				color: ${Colors.text.disabled};
			}
		`}
`;

type NavTabProps = TabProps & {
	to: To;
	target?: string;
	title?: string;
	className?: string;
	children?: ReactNode;
	onClick?: MouseEventHandler<HTMLAnchorElement>;
	[dataAttribute: `data-${string}`]: string | undefined;
};

/**
 * Tab link that renders an absolute `href` pointing at the parent shell
 * when embedded in it. The legacy app runs inside a shell iframe whose own
 * origin is the standalone legacy host, so a relative `href` (and every
 * native "open in new tab" gesture that uses it — cmd/middle-click and the
 * right-click context menu) would otherwise resolve against the legacy
 * origin instead of the shell. Plain left-clicks are handled here as
 * client-side navigation, mirroring React Router's `Link`.
 */
export function NavTab({ to, target, onClick, className, ...rest }: NavTabProps) {
	const navigate = useNavigate();
	const relativeHref = useHref(to);
	const parentOrigin = resolveTrustedParentTargetOrigin();
	const href = parentOrigin ? `${parentOrigin}${relativeHref}` : relativeHref;

	const locationPathname = useLocation().pathname.toLowerCase();
	const toPathname = useResolvedPath(to).pathname.toLowerCase();
	const isActive =
		locationPathname === toPathname ||
		(locationPathname.startsWith(toPathname) &&
			locationPathname.charAt(toPathname.length) === '/');

	function handleClick(event: MouseEvent<HTMLAnchorElement>) {
		onClick?.(event);

		const isPlainLeftClick =
			!event.defaultPrevented &&
			event.button === 0 &&
			(!target || target === '_self') &&
			!event.metaKey &&
			!event.altKey &&
			!event.ctrlKey &&
			!event.shiftKey;

		if (isPlainLeftClick) {
			event.preventDefault();
			navigate(to);
		}
	}

	return (
		<StyledNavTab
			{...rest}
			href={href}
			target={target}
			onClick={handleClick}
			className={clsx(className, isActive && 'active')}
		/>
	);
}

interface NormalTabProps {
	width?: number;
}
export const NormalTab = styled.div<NormalTabProps>`
	display: flex;
	align-items: center;
	width: ${({ width = 12.6 }) => width}rem;
	height: 7.2rem;
	position: relative;
	cursor: pointer;

	.dropdown-tab {
		height: 100%;
	}
`;

function getTabStyle({ underline_width }: TabProps) {
	return css`
		display: flex;
		align-items: center;
		height: 7.2rem;
		position: relative;
		user-select: none;
		cursor: pointer;
		text-decoration: none;
		margin: 0 1.6rem;

		div {
			width: 100%;
			display: flex;
			justify-content: center;

			::after {
				display: block;
				content: '';
				width: ${underline_width}rem;
				height: 0.2rem;
				border-radius: 0 0 0.4rem 0.4rem;
				background-color: ${Colors.primary.normal};
				position: absolute;
				bottom: 0;
				opacity: 0;
			}
		}

		@media print {
			display: none;
		}

		:hover {
			${Text} {
				color: ${Colors.primary.normal};
			}
		}
	`;
}
